import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Card, ListGroup, Badge, Alert, Spinner } from "react-bootstrap";
import { FaShare, FaSearch, FaChalkboardTeacher, FaCrown, FaUser, FaCheck, FaTimes, FaUsers, FaCamera } from "react-icons/fa";

// Constants
const GRADE_LEVELS = {
  grade_level1: "Junior Infants",
  grade_level2: "Senior Infants",
  grade_level3: "1st Class",
};

const GRADE_FILTERS = [
  { value: "all", label: "All Grades" },
  ...Object.entries(GRADE_LEVELS).map(([key, label]) => ({ value: key, label }))
];

const SNAPSHOT_OPTIONS = [
  { value: "all", label: "All Classes" },
  { value: "active", label: "Active Classes Only" },
  { value: "snapshot", label: "Snapshots Only" }
];

const TEACHER_STATUS_FILTERS = [
  { value: "all", label: "All Teachers" },
  { value: "shared", label: "Has Access" },
  { value: "not_shared", label: "No Access" },
  { value: "creator_owner", label: "Creator/Owner" }
];

// Helper functions
const getGradeInfo = (classItem) => {
  const gradeKey = Object.keys(GRADE_LEVELS).find(key => classItem[key]);
  return {
    label: gradeKey ? GRADE_LEVELS[gradeKey] : "Unknown Grade",
    key: gradeKey || "unknown"
  };
};

const extractTeacherId = (item) => typeof item === 'string' ? item : item?.id;

const transformTeacher = (teacher) => ({
  id: teacher.id,
  name: teacher.name || 'Unknown',
  email: teacher.email || '',
  active: teacher.active !== false
});

const transformClass = (classItem, teachers) => {
  const gradeInfo = getGradeInfo(classItem);
  const creator = teachers.find(t => t.id === classItem.teacherId);
  const owner = teachers.find(t => t.id === classItem.classOwnerId);

  return {
    id: classItem.id,
    name: classItem.name || 'Unnamed Class',
    grade: gradeInfo.label,
    gradeBoolean: gradeInfo.key,
    academicYear: classItem.academicYear,
    snapshot: classItem.snapshot || false,
    studentNumber: classItem.studentNumber || 0,
    teacherId: classItem.teacherId,
    classOwnerId: classItem.classOwnerId,
    creatorName: creator?.name || 'Unknown',
    ownerName: owner?.name || 'No Owner',
    hasOwner: !!classItem.classOwnerId,
    creatorIsOwner: classItem.teacherId === classItem.classOwnerId,
    active: classItem.active,
    trial_class: classItem.trial_class,
    date_updated: classItem.date_updated
  };
};

const ShareClassWithTeachersModal = ({
  show,
  onHide,
  schoolid,
  getTeachers,
  getClassesBySchoolId,
  shareClass,
  unshareClass,
  getClassSharedTeachers
}) => {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [sharedTeachers, setSharedTeachers] = useState([]);
  const [pendingChanges, setPendingChanges] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [sharedTeachersLoading, setSharedTeachersLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Filter states
  const [teacherSearchTerm, setTeacherSearchTerm] = useState("");
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [classGradeFilter, setClassGradeFilter] = useState("all");
  const [classAcademicYearFilter, setClassAcademicYearFilter] = useState("all");
  const [classSnapshotFilter, setClassSnapshotFilter] = useState("all");
  const [teacherStatusFilter, setTeacherStatusFilter] = useState("all");

  const [availableAcademicYears, setAvailableAcademicYears] = useState([
    { value: "all", label: "All Academic Years" }
  ]);

  useEffect(() => {
    if (show && schoolid) {
      loadData();
    }
  }, [show, schoolid]);

  useEffect(() => {
    if (selectedClass) {
      loadSharedTeachers();
    }
  }, [selectedClass]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [teachersResponse, classesResponse] = await Promise.all([
        getTeachers(schoolid),
        getClassesBySchoolId(schoolid)
      ]);

      const transformedTeachers = (teachersResponse || [])
        .map(transformTeacher)
        .sort((a, b) => a.name.localeCompare(b.name));

      const transformedClasses = (classesResponse || [])
        .map(classItem => transformClass(classItem, transformedTeachers))
        .sort((a, b) => {
          if (a.academicYear !== b.academicYear) {
            return b.academicYear?.localeCompare(a.academicYear || '') || 0;
          }
          return a.name.localeCompare(b.name);
        });

      const uniqueAcademicYears = [...new Set(transformedClasses
        .map(cls => cls.academicYear)
        .filter(year => year)
      )].sort().reverse();

      const academicYearOptions = [
        { value: "all", label: "All Academic Years" },
        ...uniqueAcademicYears.map(year => ({
          value: year,
          label: `${year} Academic Year`
        }))
      ];

      setAvailableAcademicYears(academicYearOptions);
      setTeachers(transformedTeachers);
      setClasses(transformedClasses);

    } catch (err) {
      setError("Failed to load data. Please try again.");
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadSharedTeachers = async () => {
    if (!selectedClass || !getClassSharedTeachers) {
      setSharedTeachers([]);
      return;
    }

    setSharedTeachersLoading(true);
    try {
      const sharedTeacherIds = await getClassSharedTeachers(selectedClass.id);
      setSharedTeachers(sharedTeacherIds || []);
    } catch (err) {
      console.error("Error loading shared teachers:", err);
      setSharedTeachers([]);
    } finally {
      setSharedTeachersLoading(false);
    }
  };

  const handleClassSelect = (classItem) => {
    setSelectedClass(classItem);
    setSharedTeachers([]);
    setPendingChanges(new Set());
    setError("");
    setSuccess("");
  };

  const isTeacherShared = (teacherId) => {
    return sharedTeachers.some(item => extractTeacherId(item) === teacherId) || 
           pendingChanges.has(`share_${teacherId}`);
  };

  const isTeacherPendingUnshare = (teacherId) => {
    return pendingChanges.has(`unshare_${teacherId}`);
  };

  const isTeacherCreatorOrOwner = (teacherId) => {
    if (!selectedClass) return false;
    return teacherId === selectedClass.teacherId || teacherId === selectedClass.classOwnerId;
  };

  const handleShareToggle = async (teacher) => {
    if (!selectedClass) return;

    if (isTeacherCreatorOrOwner(teacher.id)) {
      setError("Cannot modify access for class creator or owner.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      const isCurrentlyShared = isTeacherShared(teacher.id) && !isTeacherPendingUnshare(teacher.id);

      if (isCurrentlyShared) {
        if (unshareClass) {
          await unshareClass(selectedClass.id, teacher.id);
          setSharedTeachers(prev => prev.filter(item => extractTeacherId(item) !== teacher.id));
          setSuccess(`Removed ${teacher.name}'s access to ${selectedClass.name}`);
        } else {
          setPendingChanges(prev => new Set([...prev, `unshare_${teacher.id}`]));
          setSuccess(`Marked ${teacher.name} for removal from ${selectedClass.name}`);
        }
      } else {
        await shareClass(selectedClass.id, teacher.id);
        setSharedTeachers(prev => [...prev, teacher.id]);
        setSuccess(`Shared ${selectedClass.name} with ${teacher.name}`);
      }

    } catch (err) {
      console.error("Share/unshare error:", err);
      setError(`Failed to ${isTeacherShared(teacher.id) ? 'unshare' : 'share'} class. Please try again.`);
    } finally {
      setActionLoading(false);
    }
  };

  const getFilteredClasses = () => {
    return classes.filter(classItem => {
      const matchesGrade = classGradeFilter === "all" || classItem.gradeBoolean === classGradeFilter;
      const matchesAcademicYear = classAcademicYearFilter === "all" || classItem.academicYear === classAcademicYearFilter;

      let matchesSnapshot = true;
      if (classSnapshotFilter === "active") {
        matchesSnapshot = !classItem.snapshot;
      } else if (classSnapshotFilter === "snapshot") {
        matchesSnapshot = classItem.snapshot;
      }

      const matchesSearch = classItem.name?.toLowerCase().includes(classSearchTerm.toLowerCase()) ||
        classItem.creatorName?.toLowerCase().includes(classSearchTerm.toLowerCase()) ||
        classItem.ownerName?.toLowerCase().includes(classSearchTerm.toLowerCase());
      
      return matchesGrade && matchesAcademicYear && matchesSnapshot && matchesSearch;
    });
  };

  const getFilteredTeachers = () => {
    return teachers.filter(teacher => {
      const matchesSearch = teacher.name?.toLowerCase().includes(teacherSearchTerm.toLowerCase()) ||
        teacher.email?.toLowerCase().includes(teacherSearchTerm.toLowerCase());
      
      const isActive = teacher.active !== false;

      let matchesStatus = true;
      if (selectedClass) {
        switch (teacherStatusFilter) {
          case "shared":
            matchesStatus = isTeacherShared(teacher.id) && !isTeacherPendingUnshare(teacher.id);
            break;
          case "not_shared":
            matchesStatus = !isTeacherShared(teacher.id) || isTeacherPendingUnshare(teacher.id);
            break;
          case "creator_owner":
            matchesStatus = isTeacherCreatorOrOwner(teacher.id);
            break;
          default:
            matchesStatus = true;
        }
      }

      return matchesSearch && isActive && matchesStatus;
    });
  };

  const handleClose = () => {
    setSelectedClass(null);
    setSharedTeachers([]);
    setPendingChanges(new Set());
    setError("");
    setSuccess("");
    setTeacherSearchTerm("");
    setClassSearchTerm("");
    setClassGradeFilter("all");
    setClassAcademicYearFilter("all");
    setClassSnapshotFilter("all");
    setTeacherStatusFilter("all");
    onHide();
  };

  const filteredClasses = getFilteredClasses();
  const filteredTeachers = getFilteredTeachers();
  const sharedCount = selectedClass ? sharedTeachers.filter(item => extractTeacherId(item)).length : 0;

  const getTeacherStatusBadge = (teacher) => {
    if (!selectedClass) return null;

    const isCreator = teacher.id === selectedClass.teacherId;
    const isOwner = teacher.id === selectedClass.classOwnerId;
    const isShared = isTeacherShared(teacher.id);
    const isPendingUnshare = isTeacherPendingUnshare(teacher.id);

    if (isCreator && isOwner) return <Badge bg="success" className="me-1">Creator & Owner</Badge>;
    if (isCreator) return <Badge bg="primary" className="me-1">Creator</Badge>;
    if (isOwner) return <Badge bg="warning" className="me-1">Owner</Badge>;
    if (isPendingUnshare) return <Badge bg="danger" className="me-1">Pending Removal</Badge>;
    if (isShared) return <Badge bg="info" className="me-1">Shared</Badge>;
    
    return null;
  };

  const getActionButton = (teacher) => {
    if (!selectedClass || isTeacherCreatorOrOwner(teacher.id)) return null;

    const isShared = isTeacherShared(teacher.id);
    const isPendingUnshare = isTeacherPendingUnshare(teacher.id);

    if (isShared && !isPendingUnshare) {
      return (
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => handleShareToggle(teacher)}
          disabled={actionLoading || sharedTeachersLoading}
        >
          <FaTimes className="me-1" />
          Unshare
        </Button>
      );
    }

    return (
      <Button
        variant="outline-success"
        size="sm"
        onClick={() => handleShareToggle(teacher)}
        disabled={actionLoading || sharedTeachersLoading}
      >
        <FaCheck className="me-1" />
        Share
      </Button>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaShare className="me-2" />
          Share Classes with Teachers
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: '600px' }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Row className="h-100">
          {/* Left Panel - Classes */}
          <Col md={5} className="border-end">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                Select Class
                {selectedClass && (
                  <Badge bg="primary" className="ms-2">{selectedClass.name}</Badge>
                )}
              </h5>
            </div>

            {/* Class Filters */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Select
                  value={classAcademicYearFilter}
                  onChange={(e) => setClassAcademicYearFilter(e.target.value)}
                  size="sm"
                >
                  {availableAcademicYears.map(year => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Select
                  value={classGradeFilter}
                  onChange={(e) => setClassGradeFilter(e.target.value)}
                  size="sm"
                >
                  {GRADE_FILTERS.map(grade => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Select
                  value={classSnapshotFilter}
                  onChange={(e) => setClassSnapshotFilter(e.target.value)}
                  size="sm"
                >
                  {SNAPSHOT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={12}>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search classes..."
                    value={classSearchTerm}
                    onChange={(e) => setClassSearchTerm(e.target.value)}
                    size="sm"
                  />
                  <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </div>
              </Col>
            </Row>

            {/* Classes List */}
            <div style={{ height: '450px', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading classes...
                </div>
              ) : filteredClasses.length === 0 ? (
                <div className="text-center py-4 text-muted">No classes found</div>
              ) : (
                <div className="d-grid gap-2">
                  {filteredClasses.map(classItem => (
                    <Card
                      key={classItem.id}
                      className={`cursor-pointer ${selectedClass?.id === classItem.id ? 'border-primary bg-light' : ''}`}
                      onClick={() => handleClassSelect(classItem)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card.Body className="py-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <div className="d-flex align-items-center">
                                <div className="fw-medium me-2">{classItem.name}</div>
                                {classItem.snapshot && (
                                  <Badge bg="secondary" className="me-2">
                                    <FaCamera className="me-1" size={10} />
                                    Snapshot
                                  </Badge>
                                )}
                              </div>
                              {selectedClass?.id === classItem.id && (
                                <FaCheck className="text-primary" />
                              )}
                            </div>

                            <div className="small text-muted">
                              <div className="d-flex justify-content-between mb-1">
                                <span>
                                  {classItem.grade}
                                  {classItem.academicYear && ` • ${classItem.academicYear}`}
                                  {!classItem.snapshot && classItem.studentNumber !== undefined && (
                                    <Badge bg="light" text="dark" className="ms-2">
                                      <FaUsers className="me-1" size={10} />
                                      {classItem.studentNumber} student{classItem.studentNumber !== 1 ? 's' : ''}
                                    </Badge>
                                  )}
                                </span>
                                {classItem.trial_class && (
                                  <Badge bg="warning" className="ms-1">Fake</Badge>
                                )}
                              </div>

                              <div className="d-flex align-items-center mb-1">
                                <FaChalkboardTeacher className="me-1" />
                                <span className="me-2">Creator:</span>
                                <span className="fw-medium">{classItem.creatorName}</span>
                              </div>

                              <div className="d-flex align-items-center">
                                <FaCrown className="me-1" />
                                <span className="me-2">Owner:</span>
                                <span className={`fw-medium ${classItem.hasOwner ? 'text-dark' : 'text-muted'}`}>
                                  {classItem.ownerName}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </Col>

          {/* Right Panel - Teachers */}
          <Col md={7}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                Teachers
                {selectedClass && (
                  <Badge bg="info" className="ms-2">
                    <FaUsers className="me-1" />
                    {sharedCount} have access
                  </Badge>
                )}
              </h5>
            </div>

            {selectedClass ? (
              <>
                {/* Teacher Filters */}
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Select
                      value={teacherStatusFilter}
                      onChange={(e) => setTeacherStatusFilter(e.target.value)}
                      size="sm"
                    >
                      {TEACHER_STATUS_FILTERS.map(filter => (
                        <option key={filter.value} value={filter.value}>
                          {filter.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <div className="position-relative">
                      <Form.Control
                        type="text"
                        placeholder="Search teachers..."
                        value={teacherSearchTerm}
                        onChange={(e) => setTeacherSearchTerm(e.target.value)}
                        size="sm"
                      />
                      <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                    </div>
                  </Col>
                </Row>

                {/* Selected Class Info */}
                {selectedClass.snapshot && (
                  <Alert variant="warning" className="mb-3">
                    <FaCamera className="me-2" />
                    <strong>Snapshot Class:</strong> You are managing access to a snapshot class.
                    Snapshot classes are typically historical records.
                  </Alert>
                )}

                {/* Teachers List */}
                <div style={{ height: '400px', overflowY: 'auto' }}>
                  {sharedTeachersLoading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading teachers...
                    </div>
                  ) : filteredTeachers.length === 0 ? (
                    <div className="text-center py-4 text-muted">No teachers found</div>
                  ) : (
                    <ListGroup>
                      {filteredTeachers.map(teacher => (
                        <ListGroup.Item
                          key={teacher.id}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-1">
                              <FaUser className="me-2 text-muted" />
                              <div className="fw-medium">{teacher.name}</div>
                            </div>
                            {teacher.email && (
                              <small className="text-muted ms-4">{teacher.email}</small>
                            )}
                            <div className="mt-1">
                              {getTeacherStatusBadge(teacher)}
                            </div>
                          </div>
                          <div className="ms-2">
                            {getActionButton(teacher)}
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-5 text-muted">
                <FaChalkboardTeacher size={48} className="mb-3 opacity-50" />
                <h6>Select a class to manage teacher access</h6>
                <p className="mb-0">Choose a class from the left panel to see and manage which teachers have access to it.</p>
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            {selectedClass && (
              <span>
                Managing access for: <strong>{selectedClass.name}</strong>
                {selectedClass.snapshot && (
                  <Badge bg="secondary" className="ms-2">
                    <FaCamera className="me-1" size={10} />
                    Snapshot
                  </Badge>
                )}
                {sharedCount > 0 && (
                  <span className="ms-2">
                    ({sharedCount} teacher{sharedCount !== 1 ? 's' : ''} have access)
                  </span>
                )}
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ShareClassWithTeachersModal;